import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Mail, MessageCircle, Send, Radio } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const EMPTY = { name: '', email: '', whatsapp: '', reports: {} };

// Global Notification Hub — central recipients + per-report channel routing.
export function NotificationHub() {
  const [reports, setReports] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [status, setStatus] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get('/notification-hub/reports'),
        api.get('/notification-hub/recipients'),
        api.get('/notification-hub/status').catch(() => ({ data: {} })),
      ]);
      setReports(r1.data?.reports || []);
      setRecipients(r2.data || []);
      setStatus(r3.data || {});
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const relTime = (iso) => {
    if (!iso) return null;
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min${m > 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hr${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d > 1 ? 's' : ''} ago`;
  };

  const toggleReport = (key) => setForm((f) => ({ ...f, reports: { ...f.reports, [key]: !f.reports[key] } }));

  const addRecipient = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.email.trim() && !form.whatsapp.trim()) { toast.error('Add an email or WhatsApp number'); return; }
    setSaving(true);
    try {
      await api.post('/notification-hub/recipients', form);
      toast.success('Recipient added');
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const toggleExisting = async (rec, key) => {
    const reportsNext = { ...(rec.reports || {}), [key]: !(rec.reports || {})[key] };
    setRecipients((list) => list.map((r) => (r.id === rec.id ? { ...r, reports: reportsNext } : r)));
    try { await api.put(`/notification-hub/recipients/${rec.id}`, { reports: reportsNext }); }
    catch { toast.error('Failed to update'); load(); }
  };

  const removeRecipient = async (id) => {
    if (!window.confirm('Remove this recipient?')) return;
    try { await api.delete(`/notification-hub/recipients/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const sendNow = async (key) => {
    try { const res = await api.post(`/notification-hub/send-now/${key}`); toast.success(res.data.message || 'Sent'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card className="border-border" data-testid="notification-hub-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2"><Radio size={18} className="text-primary" />Notification Hub</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">Add recipients once, then choose which automated reports each person receives. Every alert can go by Email (Resend) and/or WhatsApp.</p>

        {/* Add recipient */}
        <div className="rounded-xl border border-border p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Name *</Label><Input value={form.name} data-testid="hub-name" onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Accountant" /></div>
            <div><Label className="flex items-center gap-1"><Mail size={12} />Email</Label><Input value={form.email} data-testid="hub-email" onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" /></div>
            <div><Label className="flex items-center gap-1"><MessageCircle size={12} />WhatsApp</Label><Input value={form.whatsapp} data-testid="hub-whatsapp" onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+9665xxxxxxxx" /></div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Reports for this recipient</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {reports.map((r) => (
                <label key={r.key} className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <Checkbox checked={!!form.reports[r.key]} onCheckedChange={() => toggleReport(r.key)} data-testid={`hub-new-report-${r.key}`} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={addRecipient} disabled={saving} className="rounded-full" data-testid="hub-add-recipient"><Plus size={14} className="mr-1.5" />{saving ? 'Adding…' : 'Add Recipient'}</Button>
        </div>

        {/* Recipients table */}
        {recipients.length === 0 ? (
          <p className="text-[13px] text-muted-foreground" data-testid="hub-empty">No recipients yet. Add one above to start routing reports.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="hub-recipients-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="pb-2 font-medium">Recipient</th>
                  {reports.map((r) => <th key={r.key} className="pb-2 font-medium text-center px-2">{r.label}</th>)}
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((rec) => (
                  <tr key={rec.id} className="border-b border-border/50" data-testid={`hub-row-${rec.id}`}>
                    <td className="py-2.5">
                      <div className="font-medium text-foreground">{rec.name}</div>
                      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-2">
                        {rec.email && <span className="flex items-center gap-1"><Mail size={10} />{rec.email}</span>}
                        {rec.whatsapp && <span className="flex items-center gap-1"><MessageCircle size={10} />{rec.whatsapp}</span>}
                      </div>
                    </td>
                    {reports.map((r) => (
                      <td key={r.key} className="py-2.5 text-center px-2">
                        <Checkbox checked={!!(rec.reports || {})[r.key]} onCheckedChange={() => toggleExisting(rec, r.key)} data-testid={`hub-toggle-${rec.id}-${r.key}`} />
                      </td>
                    ))}
                    <td className="py-2.5 text-right">
                      <Button size="sm" variant="ghost" className="h-7 text-error" onClick={() => removeRecipient(rec.id)} data-testid={`hub-remove-${rec.id}`}><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Send now */}
        {reports.length > 0 && recipients.length > 0 && (
          <div className="pt-1">
            <Label className="text-xs text-muted-foreground mb-2 block">Send a report now to all its subscribers</Label>
            <div className="flex flex-col gap-2">
              {reports.map((r) => {
                const st = status[r.key];
                return (
                  <div key={r.key} className="flex items-center gap-3 flex-wrap">
                    <Button size="sm" variant="outline" className="rounded-xl text-[12px] min-w-[190px] justify-start" onClick={() => sendNow(r.key)} data-testid={`hub-send-now-${r.key}`}><Send size={12} className="mr-1.5" />{r.label}</Button>
                    <span className="text-[11px] text-muted-foreground" data-testid={`hub-laststatus-${r.key}`}>
                      {st?.last_sent
                        ? `Last sent: ${relTime(st.last_sent)} to ${st.recipient_count} ${st.recipient_count === 1 ? 'person' : 'people'}`
                        : 'Not sent yet'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default NotificationHub;
